import React, { useState } from 'react';
import { CheckCircle2, ClipboardList, FilePlus2, Loader2, Undo2, XCircle } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import { Panel } from '../../../components/common/Panel';
import { StatCard } from '../../../components/common/StatCard';
import { RoleSwitcher } from '../../../components/admin/review/RoleSwitcher';
import { ReviewWorkflowStrip } from '../../../components/admin/review/ReviewWorkflowStrip';
import { ApplicationCharts } from '../../../components/admin/review/ApplicationCharts';
import { ApplicationFilters } from '../../../components/admin/review/ApplicationFilters';
import { ApplicationTable } from '../../../components/admin/review/ApplicationTable';
import { ReturnForCorrectionModal } from '../../../components/admin/review/ReturnForCorrectionModal';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { useApplicationList } from '../../../hooks/useApplicationList';
import { kpiValues, queueTabs } from '../../../utils/applicationQueue';
import { toBn } from '../../../utils/bn';
import type { OwnerApplication } from '../../../types/owner';
import type { QueueKey } from '../../../types/review';
export function ApplicationList() {
  const {
    applications
  } = useOwnerService();
  const list = useApplicationList();
  const [returning, setReturning] = useState<OwnerApplication | null>(null);
  const kpi = kpiValues(applications);
  const fmt = (n: number) => toBn(n.toLocaleString('en-IN'));
  const cards: {
    label: string;
    value: number;
    icon: typeof ClipboardList;
    tone: 'gray' | 'blue' | 'orange' | 'green' | 'red';
    tab: QueueKey;
  }[] = [{
    label: 'মোট আবেদন',
    value: kpi.total,
    icon: ClipboardList,
    tone: 'gray',
    tab: 'all'
  }, {
    label: 'নতুন আবেদন',
    value: kpi.new,
    icon: FilePlus2,
    tone: 'blue',
    tab: 'new'
  }, {
    label: 'যাচাই প্রক্রিয়াধীন',
    value: kpi.inReview,
    icon: Loader2,
    tone: 'orange',
    tab: 'processing'
  }, {
    label: 'অনুমোদিত',
    value: kpi.approved,
    icon: CheckCircle2,
    tone: 'green',
    tab: 'approved'
  }, {
    label: 'সংশোধনের জন্য ফেরত',
    value: kpi.returned,
    icon: Undo2,
    tone: 'orange',
    tab: 'returned'
  }, {
    label: 'বাতিল',
    value: kpi.rejected,
    icon: XCircle,
    tone: 'red',
    tab: 'rejected'
  }];
  const pick = (k: QueueKey) => {
    list.setTab(k);
    document.getElementById('application-list')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div data-id="element-3284" data-mp="3mxkmuaekulm5">
      <PageHeader eyebrow={<span className="text-xs font-semibold text-bdgreen-700" data-id="element-3286" data-mp="3n0q97hvtn9e6">তথ্য ব্যবস্থাপনা › মালিকের গাড়ির আবেদন অনুমোদন</span>} title="মালিকের গাড়ির আবেদন যাচাই ও অনুমোদন" subtitle="মালিকের মোবাইল অ্যাপ থেকে জমাকৃত সিএনজি/অটোরিকশা ও মোটরসাইকেলের আবেদন পর্যালোচনা, তথ্য যাচাই ও অনুমোদন।" actions={<RoleSwitcher data-id="element-3287" data-mp="1b8tcshw4zbd3" />} data-id="element-3285" data-mp="3mwsquyh2ilvc" />

      <ReviewWorkflowStrip data-id="element-3288" data-mp="1b8jm0s39p06c" />

      <div className="mb-5 grid gap-3 sm:grid-cols-3 xl:grid-cols-6" data-id="element-3289" data-mp="1b9cmxcfjovgk">
        {cards.map(c => <StatCard key={c.label} label={c.label} value={fmt(c.value)} icon={c.icon} tone={c.tone} onClick={() => pick(c.tab)} sub={c.tab === 'processing' ? `অনুমোদনের অপেক্ষায় ${fmt(kpi.awaiting)}` : 'তালিকা দেখতে ক্লিক করুন'} data-id="element-3290" data-mp="1g74n0tqcfnvu" />)}
      </div>

      <ApplicationCharts kpi={kpi} data-id="element-3291" data-mp="3ag9tzv4qgll3" />

      <ApplicationFilters draft={list.draft} setDraft={list.setDraft} onSearch={list.search} onReset={list.reset} onExport={list.exportExcel} data-id="element-3292" data-mp="3ahb4nyhu762a" />

      <div id="application-list" className="scroll-mt-16" data-id="element-3293" data-mp="3jgg4r991r7gk">
        <Panel bodyClassName="p-0" title="মালিকের গাড়ির আবেদন তালিকা" subtitle={`${toBn(list.rows.length)}টি আবেদন প্রদর্শিত • সারিতে ক্লিক করে বিস্তারিত দেখুন • প্রোটোটাইপ নমুনা তথ্য`} data-id="element-3294" data-mp="3jelfjiqqst6o">
          <div className="no-scrollbar flex gap-1 overflow-x-auto border-b border-border px-3 pt-2" role="tablist" aria-label="আবেদন ফিল্টার" data-id="element-3295" data-mp="3tc9kt2lz5j33">
            {queueTabs.map(t => {
            const on = list.tab === t.key;
            return <button key={t.key} role="tab" aria-selected={on} onClick={() => list.setTab(t.key)} className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 pb-2.5 pt-1.5 text-[13px] font-medium transition ${on ? 'border-navy-800 text-navy-900' : 'border-transparent text-slate-500 hover:text-navy-800'}`} data-id="element-3296" data-mp="1u2tdk5b6rsdz">
                  {t.label}
                  <span className={`rounded-full px-1.5 py-px text-[10.5px] font-semibold ${on ? 'bg-navy-800 text-white' : 'bg-slate-100 text-slate-600'}`} data-id="element-3297" data-mp="2e24cxj93dip">{toBn(list.counts[t.key] ?? 0)}</span>
                </button>;
          })}
          </div>
          <ApplicationTable rows={list.rows} officerOf={list.officerOf} onReturn={setReturning} data-id="element-3298" data-mp="bd73o62mzavx" />
        </Panel>
      </div>

      <ReturnForCorrectionModal app={returning} onClose={() => setReturning(null)} data-id="element-3299" data-mp="g7ba8o7zpgwh" />
    </div>;
}